import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  businessAreas: [],
  selected_businessArea: null,
  loading: false,
  isSuccess: false,
  recordsTotal: 0,
  errors: [],
  customFieldsLoading: false,
  lastAppliedDraw: 0,
};

export const fetchbusinessAreas = createAsyncThunk(
  'businessArea/fetchbusinessArea',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/businessarea', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchAllBusinessAreas = createAsyncThunk(
  'businessArea/fetchAllBusinessAreas',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/businessarea/all');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const getbusinessAreaById = createAsyncThunk(
  'businessArea/getbusinessAreaById',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/businessarea/${businessAreaId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addbusinessArea = createAsyncThunk(
  'businessArea/addbusinessArea',
  async (businessAreaData, { rejectWithValue, dispatch }) => {
    try {
      const params = { business_name: businessAreaData.business_name, active: businessAreaData.active };
      if (businessAreaData.customFields !== undefined) params.customFields = businessAreaData.customFields;
      if (businessAreaData.sourceLang) params.sourceLang = businessAreaData.sourceLang;
      const response = await api.post('/businessarea', params);
      dispatch(fetchbusinessAreas());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deletebusinessArea = createAsyncThunk(
  'businessarea/deleteBusinessarea',
  async (businessareaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/businessarea/${businessareaId}`);
      dispatch(fetchbusinessAreas());
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Met à jour business_name + active (sans toucher aux customFields)
export const updatebusinessArea = createAsyncThunk(
  'businessarea/updatebusinessarea',
  async (updatedbusinessarea, { rejectWithValue, dispatch }) => {
    try {
      const { id, business_name, active, sourceLang, translations } = updatedbusinessarea;
      const params = { business_name, active };
      if (sourceLang) params.sourceLang = sourceLang;
      if (translations) params.translations = translations;
      const response = await api.put(`/businessarea/${id}`, params);
      dispatch(fetchbusinessAreas()); // refresh la liste
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Charge les customFields d'une BusinessArea
export const fetchCustomFields = createAsyncThunk(
  'businessarea/fetchCustomFields',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/businessarea/${businessAreaId}/custom-fields`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Sauvegarde les customFields (PUT /businessarea/:id avec customFields)
export const saveCustomFields = createAsyncThunk(
  'businessarea/saveCustomFields',
  async ({ id, business_name, active, customFields }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/businessarea/${id}`, {
        business_name,
        active,
        customFields,
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Charge les services d'une BusinessArea (GET /services/servicesByBusiness/:id)
export const fetchServicesByBusinessArea = createAsyncThunk(
  'businessarea/fetchServicesByBusinessArea',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/services/servicesByBusiness/${businessAreaId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Crée un service lié à une BusinessArea
export const createService = createAsyncThunk(
  'businessarea/createService',
  async ({ name, business_area, active, order, sourceLang }, { rejectWithValue, dispatch }) => {
    try {
      const params = { name, business_area, active: active ?? true, order };
      if (sourceLang) params.sourceLang = sourceLang;
      const response = await api.post('/services', params);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Met à jour un service
export const updateService = createAsyncThunk(
  'businessarea/updateService',
  async ({ id, name, business_area, active, order, sourceLang, translations }, { rejectWithValue, dispatch }) => {
    try {
      const params = { name, business_area, active, order };
      if (sourceLang) params.sourceLang = sourceLang;
      if (translations) params.translations = translations;
      const response = await api.put(`/services/${id}`, params);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Supprime un service
export const deleteService = createAsyncThunk(
  'businessarea/deleteService',
  async (serviceId, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/services/${serviceId}`);
      return serviceId;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ── Area Section Templates ────────────────────────────────────────────────────

export const fetchAreaSectionTemplates = createAsyncThunk(
  'businessarea/fetchAreaSectionTemplates',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/businessarea/${businessAreaId}/area-section-templates`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addAreaSectionTemplate = createAsyncThunk(
  'businessarea/addAreaSectionTemplate',
  async ({ businessAreaId, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/businessarea/${businessAreaId}/area-section-templates`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAreaSectionTemplate = createAsyncThunk(
  'businessarea/updateAreaSectionTemplate',
  async ({ businessAreaId, key, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/businessarea/${businessAreaId}/area-section-templates/${key}`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAreaSectionTemplate = createAsyncThunk(
  'businessarea/deleteAreaSectionTemplate',
  async ({ businessAreaId, key }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/businessarea/${businessAreaId}/area-section-templates/${key}`);
      return key;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ── Menu Category Templates ───────────────────────────────────────────────────

export const fetchMenuCategoryTemplates = createAsyncThunk(
  'businessarea/fetchMenuCategoryTemplates',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/businessarea/${businessAreaId}/menu-category-templates`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMenuCategoryTemplate = createAsyncThunk(
  'businessarea/addMenuCategoryTemplate',
  async ({ businessAreaId, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/businessarea/${businessAreaId}/menu-category-templates`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMenuCategoryTemplate = createAsyncThunk(
  'businessarea/updateMenuCategoryTemplate',
  async ({ businessAreaId, key, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/businessarea/${businessAreaId}/menu-category-templates/${key}`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMenuCategoryTemplate = createAsyncThunk(
  'businessarea/deleteMenuCategoryTemplate',
  async ({ businessAreaId, key }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/businessarea/${businessAreaId}/menu-category-templates/${key}`);
      return key;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadMenuCategoryTemplateImage = createAsyncThunk(
  'businessarea/uploadMenuCategoryTemplateImage',
  async ({ businessAreaId, key, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/businessarea/${businessAreaId}/menu-category-templates/${key}/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMenuCategoryTemplateImage = createAsyncThunk(
  'businessarea/deleteMenuCategoryTemplateImage',
  async ({ businessAreaId, key }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/businessarea/${businessAreaId}/menu-category-templates/${key}/image`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ── Department Templates ──────────────────────────────────────────────────────

export const fetchDepartmentTemplates = createAsyncThunk(
  'businessarea/fetchDepartmentTemplates',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/businessarea/${businessAreaId}/department-templates`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addDepartmentTemplate = createAsyncThunk(
  'businessarea/addDepartmentTemplate',
  async ({ businessAreaId, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/businessarea/${businessAreaId}/department-templates`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateDepartmentTemplate = createAsyncThunk(
  'businessarea/updateDepartmentTemplate',
  async ({ businessAreaId, key, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/businessarea/${businessAreaId}/department-templates/${key}`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteDepartmentTemplate = createAsyncThunk(
  'businessarea/deleteDepartmentTemplate',
  async ({ businessAreaId, key }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/businessarea/${businessAreaId}/department-templates/${key}`);
      return key;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ── Evaluation Criteria Templates ───────────────────────────────────────────────

export const fetchEvaluationCriteriaTemplates = createAsyncThunk(
  'businessarea/fetchEvaluationCriteriaTemplates',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/businessarea/${businessAreaId}/evaluation-criteria-templates`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addEvaluationCriteriaTemplate = createAsyncThunk(
  'businessarea/addEvaluationCriteriaTemplate',
  async ({ businessAreaId, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/businessarea/${businessAreaId}/evaluation-criteria-templates`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEvaluationCriteriaTemplate = createAsyncThunk(
  'businessarea/updateEvaluationCriteriaTemplate',
  async ({ businessAreaId, key, template }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/businessarea/${businessAreaId}/evaluation-criteria-templates/${key}`, template);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteEvaluationCriteriaTemplate = createAsyncThunk(
  'businessarea/deleteEvaluationCriteriaTemplate',
  async ({ businessAreaId, key }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/businessarea/${businessAreaId}/evaluation-criteria-templates/${key}`);
      return key;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadBusinessAreaImage = createAsyncThunk(
  'businessarea/uploadBusinessAreaImage',
  async ({ businessAreaId, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/businessarea/${businessAreaId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeBusinessAreaImage = createAsyncThunk(
  'businessarea/removeBusinessAreaImage',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/businessarea/${businessAreaId}/image`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const businessAreaSlice = createSlice({
  name: 'businessarea',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchbusinessAreas.pending, (state) => { state.loading = true; })
      .addCase(fetchbusinessAreas.fulfilled, (state, { payload }) => {
        state.loading = false;
        // Requests can resolve out of order (e.g. a broad search fired first can respond after
        // a narrower one typed right after) — `draw` is a per-request sequence number echoed
        // back by the backend, so a response older than the last one we applied is discarded.
        const draw = payload?.draw ?? 0;
        if (draw < state.lastAppliedDraw) return;
        state.lastAppliedDraw = draw;
        state.isSuccess = true;
        state.businessAreas = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
      })
      .addCase(fetchbusinessAreas.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(fetchAllBusinessAreas.pending, (state) => { state.loading = true; })
      .addCase(fetchAllBusinessAreas.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.businessAreas = Array.isArray(payload) ? payload : (payload?.data || []);
        state.loading = false;
      })
      .addCase(fetchAllBusinessAreas.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(deletebusinessArea.pending, (state) => { state.loading = true; })
      .addCase(deletebusinessArea.fulfilled, (state) => { state.isSuccess = true; state.loading = false; })
      .addCase(deletebusinessArea.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(addbusinessArea.pending, (state) => { state.loading = true; })
      .addCase(addbusinessArea.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        const newBA = { ...payload, _DT_RowId: payload._id };
        state.businessAreas.push(newBA);
        state.loading = false;
      })
      .addCase(addbusinessArea.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(updatebusinessArea.pending, (state) => { state.loading = true; })
      .addCase(updatebusinessArea.fulfilled, (state, { payload }) => {
        state.businessAreas = state.businessAreas.map((ba) =>
          (ba.DT_RowId === payload._id || ba._id === payload._id)
            ? { ...ba, ...payload, DT_RowId: payload._id }
            : ba
        );
        state.loading = false;
      })
      .addCase(updatebusinessArea.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(uploadBusinessAreaImage.fulfilled, (state, { payload }) => {
        state.businessAreas = state.businessAreas.map((ba) =>
          (ba.DT_RowId === payload._id || ba._id === payload._id)
            ? { ...ba, ...payload, DT_RowId: payload._id }
            : ba
        );
      })
      .addCase(removeBusinessAreaImage.fulfilled, (state, { payload }) => {
        state.businessAreas = state.businessAreas.map((ba) =>
          (ba.DT_RowId === payload._id || ba._id === payload._id)
            ? { ...ba, ...payload, DT_RowId: payload._id }
            : ba
        );
      })

      .addCase(getbusinessAreaById.pending, (state) => { state.loading = true; })
      .addCase(getbusinessAreaById.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.selected_businessArea = payload;
        state.loading = false;
      })
      .addCase(getbusinessAreaById.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(fetchCustomFields.pending, (state) => { state.customFieldsLoading = true; })
      .addCase(fetchCustomFields.fulfilled, (state) => { state.customFieldsLoading = false; })
      .addCase(fetchCustomFields.rejected, (state) => { state.customFieldsLoading = false; })

      .addCase(saveCustomFields.pending, (state) => { state.customFieldsLoading = true; })
      .addCase(saveCustomFields.fulfilled, (state, { payload }) => {
        state.customFieldsLoading = false;
        state.businessAreas = state.businessAreas.map((ba) =>
          (ba.DT_RowId === payload._id || ba._id === payload._id)
            ? { ...ba, customFields: payload.customFields }
            : ba
        );
      })
      .addCase(saveCustomFields.rejected, (state) => { state.customFieldsLoading = false; });
  }
});

export default businessAreaSlice.reducer;